import { useEffect, useState } from 'react';
import { AdditionalGet, ServiceGet } from '../../../utils/type';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../../utils/currency';

type DateAndStaffProps = {
  data: ServiceGet;
  onChange: (services: string[]) => void;
  onChangeNameAndPrice: (services: { name: string; price: number }[]) => void;
};

const AdditionalDetails = ({
  data,
  onChange,
  onChangeNameAndPrice,
}: DateAndStaffProps) => {
  const { t } = useTranslation();
  const [selectedServices, setSelectedServices] = useState<string[]>([]);

  const additionalServices = data?.additionalServices || [];

  // ADD / REMOVE ADDITIONAL SERVICE
  const handleSelectAddtionalService = (id: string) => {
    if (!id) return;

    setSelectedServices(
      (prev) =>
        prev.includes(id)
          ? prev.filter((serviceId) => serviceId !== id) // remove if already selected
          : [...prev, id] // add if not selected
    );
  };

  // Propagate selected services to parent component (BookingPage)
  // when selection changes, to update additonalServiceIds state
  // which flows into the booking payload as additionalServiceIds
  useEffect(() => {
    onChange(selectedServices);

    const mappedNamesAndPrices = additionalServices
      .filter((s: AdditionalGet) => selectedServices.includes(s.id ?? ''))
      .map((s: AdditionalGet) => ({
        name: String(s.serviceItem || ''),
        price: s.price || 0,
      }));

    onChangeNameAndPrice(mappedNamesAndPrices);
  }, [selectedServices, additionalServices]);

  console.log('Selected Additional Services: ', selectedServices);

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-3">
      <div className="border-b mb-2 flex justify-between items-center">
        <p className="text-title-bold  mb-2">
          {t('booking.choose_additional_service')}
        </p>
      </div>

      {/* CARDS */}
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
        {additionalServices?.map((a: AdditionalGet, index: number) => {
          const isSelected = selectedServices.includes(a?.id ?? '');

          return (
            <button
              key={index}
              onClick={() => handleSelectAddtionalService(a?.id ?? '')}
              className={`flex flex-row items-center border-2 p-2 rounded-md transition-all ${
                isSelected
                  ? 'border-secondary bg-secondary/15 ring-2 ring-secondary/30'
                  : 'border-gray-200 bg-white hover:border-secondary/50'
              }`}
            >
              <span className="w-10 h-10 bg-gray-100 rounded-md overflow-hidden flex-shrink-0 relative">
                <img
                  src={a?.images}
                  alt={t('booking.service_alt')}
                  className="w-10 h-10 object-cover"
                />
                {isSelected && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
              </span>

              <div className="flex flex-col text-left ml-3 min-w-0">
                <p className="text-body truncate font-medium">
                  {a?.serviceItem || '-'}
                </p>
                <p
                  className={`text-body-bold ${isSelected ? 'text-secondary' : 'text-gray-600'}`}
                >
                  {formatCurrency(Number(a?.price) || 0)}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default AdditionalDetails;
